"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { AnimatePresence, motion, useInView, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Navbar from "./components/Navbar";
import UpcomingFeatures from "./components/UpcomingFeatures";
import HeroThreeScene from "./components/HeroThreeScene";
import ScrollProgress from "./components/ScrollProgress";
import { ContainerScroll } from "@/components/ui/container-scroll-animation";
import { authApi, subscriptionApi } from "@/lib/api";
import { client } from "@/sanity/lib/client";
import { urlForImage } from "@/sanity/lib/image";
import NewsletterForm from "./components/NewsletterForm";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

/* ─── helpers ─── */
function FadeIn({
  children,
  delay = 0,
  className = "",
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function DrawLine({ className = "" }: { className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    gsap.fromTo(el, { scaleX: 0 }, {
      scaleX: 1, duration: 1.2, ease: "expo.out",
      scrollTrigger: { trigger: el, start: "top 85%" },
    });
  }, []);
  return (
    <div ref={ref} className={`origin-center ${className}`}
      style={{ height: 1, background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.5), transparent)" }} />
  );
}

/* ─── icons ─── */
const Icons = {
  Arrow: () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  ),
  Lock: () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 11V7a5 5 0 0110 0v4" />
    </svg>
  ),
  Check: () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  ),
  Moon: () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
    </svg>
  ),
  Clock: () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <circle cx="12" cy="12" r="10" /><path strokeLinecap="round" d="M12 6v6l4 2" />
    </svg>
  ),
  Users: () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
    </svg>
  ),
  Link: () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
    </svg>
  ),
  Sliders: () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
      <circle cx="8" cy="6" r="2" fill="currentColor" stroke="none" />
      <circle cx="16" cy="12" r="2" fill="currentColor" stroke="none" />
      <circle cx="10" cy="18" r="2" fill="currentColor" stroke="none" />
    </svg>
  ),
  Bot: () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <rect x="3" y="11" width="18" height="10" rx="2" />
      <circle cx="12" cy="5" r="2" />
      <path strokeLinecap="round" d="M12 7v4M8 15h.01M16 15h.01" />
    </svg>
  ),
  Search: () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <circle cx="11" cy="11" r="8" /><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35" />
    </svg>
  ),
  Message: () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  ),
  Comment: () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
    </svg>
  ),
  Chat: () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h8M8 13h5m-9 5.5L7 16h11a2 2 0 002-2V6a2 2 0 00-2-2H4a2 2 0 00-2 2v12.5z" />
      <circle cx="18" cy="6" r="3" fill="currentColor" stroke="none" />
    </svg>
  ),
  Grid: () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  Gauge: () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 2a10 10 0 100 20A10 10 0 0012 2zm0 0v4m0 0a6 6 0 016 6H6a6 6 0 016-6zm0 6l2 2" />
    </svg>
  ),
  Chart: () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  Shield: () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  CreditCard: () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <rect x="1" y="4" width="22" height="16" rx="2" />
      <path strokeLinecap="round" d="M1 10h22" />
    </svg>
  ),
  Minus: () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" d="M5 12h14" />
    </svg>
  ),
};

/* ─── shared UI ─── */
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs tracking-[0.3em] uppercase mb-5 flex items-center justify-center gap-3"
      style={{ color: "#C9A84C", fontFamily: "'Montserrat', sans-serif" }}>
      <span className="h-px w-6 inline-block" style={{ background: "#C9A84C" }} />
      {children}
      <span className="h-px w-6 inline-block" style={{ background: "#C9A84C" }} />
    </p>
  );
}

function SectionHeadline({ children, id }: { children: React.ReactNode; id?: string }) {
  return (
    <h2 id={id} style={{
      fontFamily: "'Cormorant', Georgia, serif",
      fontSize: "clamp(2rem, 4.5vw, 3.25rem)",
      fontWeight: 400, color: "#F5F0E8", lineHeight: 1.1,
    }}>
      {children}
    </h2>
  );
}

function GoldDivider() {
  return (
    <div className="w-10 h-px mx-auto"
      style={{ background: "linear-gradient(90deg, transparent, #C9A84C, transparent)" }} />
  );
}

/* ─── data ─── */
const PROBLEMS = [
  {
    icon: <Icons.Moon />,
    title: "You Sleep, You Lose",
    desc: "While you sleep, competitors are answering every DM and winning every lead.",
  },
  {
    icon: <Icons.Clock />,
    title: "Inconsistent Follow-Ups",
    desc: "Manual replies mean slow response times. 57% of consumers expect a response within an hour.",
  },
  {
    icon: <Icons.Users />,
    title: "No Scale",
    desc: "You can't manually qualify hundreds of conversations. Your growth is capped.",
  },
];

const HOW_IT_WORKS = [
  {
    step: "01",
    icon: <Icons.Link />,
    title: "One-Click OAuth Connection",
    desc: "Connect your Instagram business account in seconds. We use Instagram's official API — no password required, no security risks. Your account stays 100% yours.",
    points: ["Official Instagram Graph API", "OAuth 2.0 — no password storage", "Token auto-refresh (60-day tokens)", "Secure webhook integration"],
  },
  {
    step: "02",
    icon: <Icons.Sliders />,
    title: "Configure Your Automation",
    desc: "Define which keywords trigger which responses. Choose between instant fixed replies or custom prompt-based replies. Set your response templates per trigger.",
    points: ['Keyword matching (e.g. "pricing", "demo")', "Fixed message or prompt-based reply", "Custom comment reply text", "Multiple triggers: DM + comments"],
  },
  {
    step: "03",
    icon: <Icons.Bot />,
    title: "Watch It Respond For You",
    desc: "Every new message gets an instant, personalized response. You focus on closing deals — we handle the follow-up.",
    points: ["Keyword triggers fire instantly", "Messages logged for analytics", "Daily usage tracked automatically", "Full override control from dashboard"],
  },
];

const FEATURES = [
  {
    icon: <Icons.Search />,
    title: "Smart Keyword Matching",
    desc: "Set up keywords that matter. When someone asks about pricing, demo, or support — they get the right response instantly. No manual routing needed.",
  },
  {
    icon: <Icons.Message />,
    title: "Flexible Response Options",
    desc: "Choose how you reply. From simple text to rich cards with buttons, images, and CTAs. Guide prospects to your calendar, website, or offer.",
  },
  {
    icon: <Icons.Comment />,
    title: "Never Miss a Comment",
    desc: "Every comment on your posts gets a thoughtful response. Turn engagement into conversations, conversations into customers.",
  },
  {
    icon: <Icons.Chat />,
    title: "In-App Creator Chat — Now Available",
    desc: "Message connected creators in real-time, right inside Aurelius & Co. Discover collaborators by niche, send collab requests, and chat without leaving the dashboard.",
  },
  {
    icon: <Icons.Grid />,
    title: "One Dashboard, Multiple Accounts",
    desc: "Manage all your Instagram accounts from a single login. Perfect for agencies, brands, and creators with multiple profiles.",
  },
  {
    icon: <Icons.Gauge />,
    title: "Built-In Rate Protection",
    desc: "We respect Instagram's rules so your account stays safe. Built-in rate limiting and daily caps protect your account from flags.",
  },
  {
    icon: <Icons.Chart />,
    title: "Know What's Working",
    desc: "Track every message, response, and conversion. See reply rates and identify your best-performing automations.",
  },
  {
    icon: <Icons.Shield />,
    title: "Instagram-Approved Architecture",
    desc: "We use Instagram's official Graph API with proper OAuth. No risky workarounds, no password storage, no account bans.",
  },
  {
    icon: <Icons.CreditCard />,
    title: "Simple, Transparent Pricing",
    desc: "Choose the plan that fits your scale. Monthly or annual billing. Upgrade, downgrade, or cancel anytime.",
  },
];

// Dynamic data will be fetched via API
// const PRICING = [...]
// const COMPARISON = [...]

const FAQS = [
  {
    q: "Is Instagram DM automation against Instagram's terms of service?",
    a: "Not when done correctly. Aurelius & Co runs exclusively on Instagram's official Graph API with OAuth 2.0 — the same infrastructure Meta certifies for business tools. We never store your password, never simulate human interaction, and all reply patterns stay within Instagram's published rate limits. You're not using a bot. You're using a platform built on the approved API.",
  },
  {
    q: "Will automating Instagram DMs get my account banned?",
    a: "No — because we don't use the techniques that get accounts banned. Third-party bots that scrape or simulate browser sessions are what Instagram penalises. Aurelius & Co uses the official API with proper rate limiting and daily caps, so your account activity looks exactly like what it is: a legitimate business tool. Thousands of brands use Instagram's API for automation without any issues.",
  },
  {
    q: "What exactly is Instagram DM automation and how does it work?",
    a: "Instagram DM automation means setting up rules that automatically reply to direct messages or comments when specific keywords are detected. You configure a trigger keyword (like 'price', 'info', or 'collab') and a reply template. When a user sends that keyword, they get your pre-written response instantly — without you needing to manually check your inbox. Aurelius & Co handles all of this through Instagram's official messaging API.",
  },
  {
    q: "Can I automate replies to Instagram comments too?",
    a: "Yes. You can set up automation rules that trigger on post comments, not just DMs. When someone comments a keyword on your post, you can auto-reply to the comment, send them a DM, or both. This is especially powerful for posts where you ask people to 'comment INFO' or 'DM PRICING'.",
  },
  {
    q: "How many Instagram accounts can I connect?",
    a: "It depends on your plan. Each plan tier supports a different number of connected Instagram business accounts. You can view the exact limits on the pricing section of our homepage and upgrade at any time from your dashboard.",
  },
  {
    q: "What happens when I hit my daily automated reply limit?",
    a: "Your automation pauses automatically for the remainder of that day. We track daily usage per account in real time. You can see your current usage from the dashboard, upgrade your plan for higher limits, or simply wait for the counter to reset at midnight.",
  },
  {
    q: "Do I need to give you my Instagram password?",
    a: "Never. We connect to your Instagram account using OAuth 2.0 — the same standard login flow used by all official Instagram integrations. You log in through Instagram's own interface, we receive a revocable access token, and that's it. We have no access to your password and never will. You can disconnect your account at any time.",
  },
  {
    q: "Can I turn off automations or override a reply?",
    a: "Yes — you have complete control. Every automation rule can be paused, edited, or deleted from your dashboard in real time. Nothing runs without you setting it up first, and you can disable any rule instantly. Your inbox, your rules.",
  },
];

const SOFTWARE_APPLICATION_SCHEMA = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Aurelius & Co",
  url: "https://www.aureliusco.in",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description: "Instagram DM automation tool that auto-replies to messages and comments using keyword triggers. Built on Instagram's official Graph API — safe, compliant, and 24/7.",
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    description: "Free plan with 50 automated replies per day",
  },
});

const FAQ_SCHEMA = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((faq) => ({
    "@type": "Question",
    name: faq.q,
    acceptedAnswer: { "@type": "Answer", text: faq.a },
  })),
});

/* ─── cell helper for comparison table ─── */
function Cell({ val }: { val: string | boolean }) {
  if (typeof val === "boolean") {
    return val
      ? <span style={{ color: "#C9A84C" }}><Icons.Check /></span>
      : <span style={{ color: "rgba(245,240,232,0.18)" }}><Icons.Minus /></span>;
  }
  return <span style={{ color: "rgba(245,240,232,0.55)", fontFamily: "'Montserrat', sans-serif", fontSize: "0.75rem" }}>{val}</span>;
}

/* ─── mockup data ─── */
const IG_HANDLES = [
  "thestylehaus", "nomadlens.co", "brandxstudio", "velvetroast",
  "urbanbloomco", "solcreatives", "driftwoodmkt", "luminarybrand",
  "peakstrategy", "inkandoak", "goldenvine.co", "thecraftedge",
  "meridianhaus", "boldseasonco", "thepressroom_", "nocturnalco",
  "copperlattice", "sixthsense.co", "harborwave_", "slatestudio",
];

function randomBetween(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function fmtNum(n: number) {
  return n >= 1000 ? (n / 1000).toFixed(1) + "k" : String(n);
}

function useMockupData() {
  const [data, setData] = useState<{
    handle: string;
    messages: string;
    rate: string;
    conversions: string;
    msgDelta: string;
    rateDelta: string;
    convDelta: string;
    bars: number[];
    hotLeads: { name: string; msg: string; ago: string }[];
  } | null>(null);

  useEffect(() => {
    const MSGS = ["pricing?", "how to sign up?", "do you ship?", "collab?", "demo pls", "tell me more", "interested!", "what's the cost?", "DM sent!", "need info", "can I get a quote?", "love this!", "how does it work?", "sign me up", "free trial?"];
    function generate() {
      const handle = IG_HANDLES[randomBetween(0, IG_HANDLES.length - 1)];
      const messages = fmtNum(randomBetween(1200, 9800));
      const rate = randomBetween(72, 99) + "%";
      const conversions = fmtNum(randomBetween(80, 740));
      const msgDelta = "+" + randomBetween(3, 18) + "% vs last month";
      const rateDelta = "avg " + randomBetween(4, 59) + "s reply time";
      const convDelta = "+" + randomBetween(1, 12) + " today";
      const bars = Array.from({ length: 14 }, () => randomBetween(18, 100));
      const hotLeads = [
        { name: "@" + IG_HANDLES[randomBetween(0, 19)], msg: MSGS[randomBetween(0, MSGS.length - 1)], ago: randomBetween(1, 59) + "s ago" },
        { name: "@" + IG_HANDLES[randomBetween(0, 19)], msg: MSGS[randomBetween(0, MSGS.length - 1)], ago: randomBetween(1, 5) + "m ago" },
        { name: "@" + IG_HANDLES[randomBetween(0, 19)], msg: MSGS[randomBetween(0, MSGS.length - 1)], ago: randomBetween(6, 29) + "m ago" },
      ];
      setData({ handle, messages, rate, conversions, msgDelta, rateDelta, convDelta, bars, hotLeads });
    }
    generate();
    const id = setInterval(generate, 3000);
    return () => clearInterval(id);
  }, []);

  return data;
}

/* ─── main ─── */
export default function LandingPage() {
  const router = useRouter();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [isDesktopConfirmed, setIsDesktopConfirmed] = useState(false);
  const mockup = useMockupData();

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    setIsDesktopConfirmed(!mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsDesktopConfirmed(!e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  const [latestPosts, setLatestPosts] = useState<any[]>([]);
  const [postsLoading, setPostsLoading] = useState(true);
  useEffect(() => {
    client.fetch(`*[_type == "post"] | order(publishedAt desc) [0...3] {
      _id, title, slug, mainImage, publishedAt, excerpt,
      "categories": categories[]->{title}
    }`).then(posts => { setLatestPosts(posts); setPostsLoading(false); }).catch(() => setPostsLoading(false));
  }, []);

  useEffect(() => {
    authApi.me().then(({ data: user }) => {
      setIsLoggedIn(!!user);
    }).catch(() => setIsLoggedIn(false));
  }, []);

  /* scroll-driven parallax for sections */
  const { scrollY } = useScroll();
  const problemParallax = useTransform(scrollY, [400, 1200], [40, 0]);
  const featuresParallax = useTransform(scrollY, [1800, 3000], [60, 0]);

  /* hero is now Three.js powered – no more mouse tilt needed for canvas */
  const heroRef = useRef<HTMLElement>(null);
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);

  /* GSAP: stagger section headings on scroll */
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    // Configure ScrollTrigger to respect user preferences
    ScrollTrigger.config({
      ignoreMobileResize: true,
      autoRefreshEvents: "visibilitychange,DOMContentLoaded,load,resize",
    });

    /* section labels + headlines */
    gsap.utils.toArray<HTMLElement>("[data-gsap='reveal']").forEach((el) => {
      gsap.fromTo(el,
        { opacity: 0, y: 45, filter: "blur(4px)" },
        {
          opacity: 1, y: 0, filter: "blur(0px)",
          duration: 1.1, ease: "expo.out",
          scrollTrigger: { trigger: el, start: "top 88%", toggleActions: "play none none none" },
        },
      );
    });

    /* how-it-works cards stagger */
    gsap.utils.toArray<HTMLElement>("[data-gsap='card']").forEach((el, i) => {
      gsap.fromTo(el,
        { opacity: 0, x: i % 2 === 0 ? -40 : 40 },
        {
          opacity: 1, x: 0,
          duration: 0.9, ease: "easeOut",
          scrollTrigger: { trigger: el, start: "top 86%" },
        },
      );
    });

    /* features — horizontal scroll: pin panel, map vertical scroll → track scrollLeft.
       Desktop only; mobile + reduced-motion fall back to native horizontal swipe. */
    const panel = document.querySelector<HTMLElement>("[data-gsap='hfeat-panel']");
    const track = document.querySelector<HTMLElement>("[data-gsap='hfeat-track']");
    if (panel && track && window.matchMedia("(min-width: 1024px)").matches) {
      const maxX = () => track.scrollWidth - track.clientWidth;
      ScrollTrigger.create({
        trigger: panel,
        start: "top 96px",
        end: () => "+=" + maxX(),
        pin: true,
        scrub: 1,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onUpdate: (self) => { track.scrollLeft = self.progress * maxX(); },
      });
    }

    return () => ScrollTrigger.getAll().forEach((t) => t.kill());
  }, []);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: SOFTWARE_APPLICATION_SCHEMA,
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: FAQ_SCHEMA,
        }}
      />

      <div className="min-h-screen overflow-x-hidden" style={{ background: "#0A0908", color: "#F5F0E8" }}>
        <ScrollProgress />
        <Navbar />

        <main>
        {/* ══════════════════════════════════════
            HERO
        ══════════════════════════════════════ */}
        <section
          ref={heroRef}
          className="relative min-h-screen flex flex-col items-center justify-center pt-24 pb-32 px-6 text-center overflow-hidden"
          aria-label="Hero"
        >
          {isDesktopConfirmed ? (
            <HeroThreeScene />
          ) : (
            <div
              aria-hidden="true"
              className="absolute inset-0 pointer-events-none"
              style={{ background: "radial-gradient(circle at 50% 40%, rgba(201,168,76,0.12), transparent 60%)" }}
            />
          )}

          {/* Eyebrow */}
          <motion.div
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 flex items-center gap-3 mb-10"
          >
            <div className="h-px w-8" style={{ background: "#C9A84C" }} />
            <span className="text-xs font-medium tracking-[0.25em] uppercase"
              style={{ color: "#C9A84C", fontFamily: "'Montserrat', sans-serif" }}>
              Instagram DM Automation Tool
            </span>
            <div className="h-px w-8" style={{ background: "#C9A84C" }} />
          </motion.div>

          {/* Headline — word-by-word stagger */}
          <motion.h1
            className="relative z-10 max-w-5xl"
            style={{
              fontFamily: "'Cormorant', Georgia, serif",
              fontSize: "clamp(3rem, 7vw, 5.75rem)",
              fontWeight: 400, lineHeight: 1.05, letterSpacing: "-0.01em", color: "#F5F0E8",
            }}
          >
            {["Turn", "every", "DM", "into"].map((word, i) => (
              <motion.span
                key={word + i}
                style={{ display: "inline-block", marginRight: "0.28em", color: word === "every" ? "#C9A84C" : undefined, fontStyle: word === "every" ? "italic" : undefined }}
                initial={{ opacity: 0, y: 40, rotateX: -25 }}
                animate={{ opacity: 1, y: 0, rotateX: 0 }}
                transition={{ duration: 0.8, delay: 0.1 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              >
                {word}
              </motion.span>
            ))}
            <br />
            {["your", "next", "client"].map((word, i) => (
              <motion.span
                key={word + i}
                style={{ display: "inline-block", marginRight: "0.28em" }}
                initial={{ opacity: 0, y: 40, rotateX: -25 }}
                animate={{ opacity: 1, y: 0, rotateX: 0 }}
                transition={{ duration: 0.8, delay: 0.5 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              >
                {word}
              </motion.span>
            ))}
          </motion.h1>

          {/* Sub */}
          <motion.p
            initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 mt-7 max-w-lg text-base leading-relaxed"
            style={{ color: "rgba(245,240,232,0.5)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}
          >
            Automate Instagram DMs and comment replies with keyword triggers. Built on Instagram's official API — safe, compliant, and working 24/7.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 mt-11 flex flex-col sm:flex-row items-center gap-4"
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              style={{ position: "relative" }}
            >
              <Link
                href={isLoggedIn ? "/dashboard" : "/login"}
                className="group flex items-center gap-2.5 px-9 py-3.5 text-xs font-medium tracking-[0.12em] uppercase"
                style={{
                  background: "linear-gradient(135deg, #C9A84C 0%, #E8D5A3 50%, #C9A84C 100%)",
                  color: "#0A0908", fontFamily: "'Montserrat', sans-serif",
                  backgroundSize: "200% 100%",
                  transition: "background-position 0.5s ease",
                }}>
                {isLoggedIn ? "Go to Dashboard" : "Start Free Trial"}
                <motion.span
                  animate={{ x: [0, 4, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                >
                  <Icons.Arrow />
                </motion.span>
              </Link>
            </motion.div>
            <motion.a
              href="#how-it-works"
              whileHover={{ borderColor: "rgba(201,168,76,0.6)", backgroundColor: "rgba(255,255,255,0.04)" }}
              className="flex items-center gap-2 px-9 py-3.5 text-xs font-medium tracking-[0.12em] uppercase border"
              style={{ borderColor: "rgba(201,168,76,0.3)", color: "rgba(245,240,232,0.6)", fontFamily: "'Montserrat', sans-serif", cursor: "pointer" }}>
              See How It Works
            </motion.a>
          </motion.div>

          {/* Trust */}
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="relative z-10 mt-7 flex items-center gap-2 text-xs"
            style={{ color: "rgba(201,168,76,0.4)", fontFamily: "'Montserrat', sans-serif" }}>
            <Icons.Lock />
            OAuth only — no passwords stored, ever
          </motion.p>
        </section>

        {/* ══════════════════════════════════════
            DASHBOARD — ContainerScroll 3-D tilt
        ══════════════════════════════════════ */}
        <ContainerScroll
          titleComponent={
            <p className="text-xs tracking-[0.25em] uppercase mb-4"
              style={{ color: "rgba(201,168,76,0.5)", fontFamily: "'Montserrat', sans-serif" }}>
              Your live dashboard
            </p>
          }
        >
          {/* inner dashboard — dark bg fills the Card */}
          <div className="h-full w-full flex flex-col" style={{ background: "rgba(10,9,8,0.97)" }}>
            {/* Chrome bar */}
            <div className="flex items-center gap-2 px-5 py-3 flex-shrink-0"
              style={{ borderBottom: "1px solid rgba(201,168,76,0.1)", background: "rgba(10,9,8,0.9)" }}>
              <div className="flex gap-1.5">
                {["rgba(255,90,80,0.45)", "rgba(255,185,50,0.45)", "rgba(40,200,80,0.45)"].map((c, i) => (
                  <div key={i} className="w-2.5 h-2.5 rounded-full" style={{ background: c }} />
                ))}
              </div>
              <div className="flex-1 flex justify-center">
                <div className="px-5 py-0.5 text-xs" style={{
                  background: "rgba(255,255,255,0.03)", color: "rgba(201,168,76,0.4)",
                  fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.05em",
                  border: "1px solid rgba(201,168,76,0.08)",
                }}>
                  aureliusco.in/dashboard
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 p-5 sm:p-6 overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="text-xs mb-0.5" style={{ color: "rgba(245,240,232,0.25)", fontFamily: "'Montserrat', sans-serif" }}>Overview · Last 30 days</p>
                  <p className="transition-all duration-700" style={{ color: "#F5F0E8", fontFamily: "'Cormorant', Georgia, serif", fontSize: "1.15rem" }}>
                    @{mockup?.handle ?? "yourbrand"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#C9A84C" }} />
                  <div className="px-3 py-1 text-xs" style={{
                    border: "1px solid rgba(201,168,76,0.25)", color: "#C9A84C",
                    fontFamily: "'Montserrat', sans-serif", fontSize: "0.65rem", letterSpacing: "0.1em",
                  }}>LIVE</div>
                </div>
              </div>

              {/* Stat cards */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                {[
                  { label: "Messages Delivered", val: mockup?.messages ?? "—", delta: mockup?.msgDelta ?? "—" },
                  { label: "Response Rate", val: mockup?.rate ?? "—", delta: mockup?.rateDelta ?? "—" },
                  { label: "Conversions", val: mockup?.conversions ?? "—", delta: mockup?.convDelta ?? "—" },
                ].map((s) => (
                  <div key={s.label} className="p-4 transition-all duration-700"
                    style={{ background: "rgba(255,255,255,0.018)", border: "1px solid rgba(201,168,76,0.09)" }}>
                    <p className="mb-1.5" style={{ color: "rgba(245,240,232,0.3)", fontFamily: "'Montserrat', sans-serif", fontSize: "0.6rem", letterSpacing: "0.05em" }}>{s.label}</p>
                    <p className="transition-all duration-500"
                      style={{ fontFamily: "'Cormorant', Georgia, serif", fontSize: "1.9rem", fontWeight: 300, color: "#F5F0E8", lineHeight: 1, height: "2rem", overflow: "hidden" }}>
                      {s.val}
                    </p>
                    <p className="mt-1.5" style={{ color: "rgba(201,168,76,0.5)", fontFamily: "'Montserrat', sans-serif", fontSize: "0.58rem", letterSpacing: "0.06em" }}>
                      {s.delta}
                    </p>
                  </div>
                ))}
              </div>

              {/* Chart */}
              <div className="p-4 mb-3 flex flex-col" style={{ background: "rgba(255,255,255,0.012)", border: "1px solid rgba(201,168,76,0.07)", height: 96 }}>
                <p className="mb-2" style={{ color: "rgba(245,240,232,0.2)", fontFamily: "'Montserrat', sans-serif", fontSize: "0.58rem", letterSpacing: "0.1em" }}>DM ENGAGEMENT · 14-DAY TREND</p>
                <div className="flex items-end gap-1 flex-1">
                  {(mockup?.bars ?? Array(14).fill(50)).map((h, i, arr) => (
                    <div key={i} className="flex-1 transition-all duration-700" style={{
                      height: `${h}%`,
                      background: i >= arr.length - 3
                        ? "linear-gradient(to top, #C9A84C, rgba(232,213,163,0.85))"
                        : "rgba(201,168,76,0.14)",
                      opacity: i >= arr.length - 3 ? 1 : 0.55 + (i / arr.length) * 0.35,
                    }} />
                  ))}
                </div>
              </div>

              {/* Hot leads feed */}
              <div style={{ border: "1px solid rgba(201,168,76,0.07)" }}>
                <p className="px-3 pt-2.5 pb-1.5" style={{ color: "rgba(245,240,232,0.2)", fontFamily: "'Montserrat', sans-serif", fontSize: "0.58rem", letterSpacing: "0.1em" }}>
                  RECENT LEADS · AUTO-REPLIED
                </p>
                {(mockup?.hotLeads ?? []).map((lead, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 transition-all duration-700"
                    style={{ borderTop: "1px solid rgba(201,168,76,0.05)" }}>
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: "rgba(201,168,76,0.12)", border: "1px solid rgba(201,168,76,0.2)" }}>
                        <span style={{ color: "#C9A84C", fontSize: "0.5rem", fontFamily: "'Cormorant', serif", fontWeight: 600 }}>
                          {lead.name[1].toUpperCase()}
                        </span>
                      </div>
                      <span className="truncate max-w-[90px]" style={{ color: "rgba(245,240,232,0.6)", fontFamily: "'Montserrat', sans-serif", fontSize: "0.65rem" }}>{lead.name}</span>
                      <span className="truncate max-w-[120px]" style={{ color: "rgba(245,240,232,0.25)", fontFamily: "'Montserrat', sans-serif", fontSize: "0.6rem" }}>"{lead.msg}"</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="w-1 h-1 rounded-full" style={{ background: "#C9A84C" }} />
                      <span style={{ color: "rgba(201,168,76,0.45)", fontFamily: "'Montserrat', sans-serif", fontSize: "0.58rem" }}>{lead.ago}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ContainerScroll>

        <DrawLine className="max-w-3xl mx-auto px-6" />

        {/* ══════════════════════════════════════
            PROBLEM / SOLUTION
        ══════════════════════════════════════ */}
        <section id="problem" className="py-28 px-6" aria-labelledby="problem-heading">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-4" data-gsap="reveal">
              <SectionLabel>The Problem</SectionLabel>
              <SectionHeadline id="problem-heading">
                Your competitors are automating.{" "}
                <em style={{ color: "#C9A84C" }}>Are you?</em>
              </SectionHeadline>
            </div>
            <div className="text-center mb-16" data-gsap="reveal">
              <p className="mt-5 text-sm max-w-lg mx-auto leading-relaxed"
                style={{ color: "rgba(245,240,232,0.45)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
                Instagram has 2 billion users. Your dream clients are there. But you can't be online 24/7.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-px" style={{ background: "rgba(201,168,76,0.07)" }}>
              {PROBLEMS.map((p, i) => (
                <FadeIn key={p.title} delay={i * 0.1}>
                  <div className="p-8 cursor-default transition-all duration-500 group" style={{ background: "#0A0908" }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = "rgba(201,168,76,0.035)"; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = "#0A0908"; }}>
                    <div className="mb-5 w-12 h-12 flex items-center justify-center"
                      style={{ border: "1px solid rgba(201,168,76,0.22)", color: "#C9A84C" }}>
                      {p.icon}
                    </div>
                    <h3 className="text-sm font-medium mb-3"
                      style={{ color: "#F5F0E8", fontFamily: "'Montserrat', sans-serif" }}>{p.title}</h3>
                    <p className="text-sm leading-relaxed"
                      style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>{p.desc}</p>
                  </div>
                </FadeIn>
              ))}
            </div>

            <FadeIn delay={0.3} className="mt-14 text-center">
              <p className="text-base max-w-2xl mx-auto leading-relaxed"
                style={{ color: "rgba(245,240,232,0.6)", fontFamily: "'Cormorant', Georgia, serif", fontSize: "1.25rem", fontStyle: "italic" }}>
                "That's why we built Aurelius & Co — your Automated Reply System that never sleeps, never misses, and never stops responding."
              </p>
            </FadeIn>
          </div>
        </section>

        <DrawLine className="max-w-3xl mx-auto px-6" />

        {/* ══════════════════════════════════════
            HOW IT WORKS
        ══════════════════════════════════════ */}
        <section id="how-it-works" className="py-28 px-6" aria-labelledby="hiw-heading">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16" data-gsap="reveal">
              <SectionLabel>How It Works</SectionLabel>
              <SectionHeadline id="hiw-heading">
                From setup to automation{" "}
                <em style={{ color: "#C9A84C" }}>in 3 steps</em>
              </SectionHeadline>
              <div className="mt-6"><GoldDivider /></div>
            </div>

            <div className="flex flex-col gap-6">
              {HOW_IT_WORKS.map((step, i) => (
                <div key={step.step} data-gsap="card">
                  <div className="flex flex-col md:flex-row gap-6 p-8 transition-all duration-300 cursor-default"
                    style={{ border: "1px solid rgba(201,168,76,0.1)" }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(201,168,76,0.28)"; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(201,168,76,0.1)"; }}>
                    <div className="flex-shrink-0 flex items-start gap-5">
                      <div className="w-14 h-14 flex items-center justify-center flex-shrink-0"
                        style={{
                          border: "1px solid rgba(201,168,76,0.28)",
                          fontFamily: "'Cormorant', Georgia, serif",
                          fontSize: "1.2rem", fontWeight: 300, color: "#C9A84C",
                        }}>
                        {step.step}
                      </div>
                      <div className="md:hidden w-10 h-10 flex items-center justify-center flex-shrink-0"
                        style={{ border: "1px solid rgba(201,168,76,0.15)", color: "#C9A84C" }}>
                        {step.icon}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-medium mb-2"
                        style={{ color: "#F5F0E8", fontFamily: "'Montserrat', sans-serif" }}>{step.title}</h3>
                      <p className="text-sm leading-relaxed mb-4"
                        style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>{step.desc}</p>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {step.points.map((pt) => (
                          <li key={pt} className="flex items-start gap-2 text-xs"
                            style={{ color: "rgba(245,240,232,0.35)", fontFamily: "'Montserrat', sans-serif" }}>
                            <span className="flex-shrink-0 mt-0.5" style={{ color: "#C9A84C" }}><Icons.Check /></span>
                            {pt}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="hidden md:flex w-12 h-12 items-center justify-center flex-shrink-0 self-start"
                      style={{ border: "1px solid rgba(201,168,76,0.15)", color: "#C9A84C" }}>
                      {step.icon}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <DrawLine className="max-w-3xl mx-auto px-6" />

        {/* ══════════════════════════════════════
            FEATURES
        ══════════════════════════════════════ */}
        <section id="features" className="py-28 overflow-x-clip" aria-labelledby="features-heading">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12" data-gsap="reveal">
              <SectionLabel>Features</SectionLabel>
              <SectionHeadline id="features-heading">
                Everything you need to automate Instagram{" "}
                <em style={{ color: "#C9A84C" }}>at scale</em>
              </SectionHeadline>
              <div className="mt-6"><GoldDivider /></div>
              <p className="mt-6 text-sm max-w-md mx-auto leading-relaxed"
                style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
                Every feature built on Instagram's official API. No shortcuts, no account risks.
              </p>
              <p className="mt-7 inline-flex items-center gap-2 text-[0.65rem] tracking-[0.25em] uppercase"
                style={{ color: "rgba(245,240,232,0.35)", fontFamily: "'Montserrat', sans-serif" }}>
                Scroll to explore
                <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4" stroke="#C9A84C" strokeWidth={1.5} aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </p>
            </div>
          </div>

          {/* horizontal scroll panel (pinned on desktop, swipe on mobile) */}
          <div data-gsap="hfeat-panel" className="relative">
            <div
              data-gsap="hfeat-track"
              className="flex gap-5 sm:gap-6 overflow-x-auto no-scrollbar pt-8 lg:pt-14 pb-4 lg:pb-6 px-6 lg:pl-[max(1.5rem,calc((100vw-72rem)/2))] lg:pr-[max(1.5rem,calc((100vw-72rem)/2))] snap-x snap-mandatory lg:snap-none"
              style={{ scrollBehavior: "auto" }}
            >
              {FEATURES.map((feat, i) => (
                <article
                  key={feat.title}
                  className="group relative shrink-0 w-[270px] sm:w-[330px] snap-start flex flex-col p-7 cursor-default transition-transform duration-300 ease-out hover:-translate-y-2"
                  style={{ border: "1px solid rgba(201,168,76,0.12)", background: "#0A0908" }}
                >
                  {/* hover border + glow */}
                  <span aria-hidden className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                    style={{ border: "1px solid rgba(201,168,76,0.45)", boxShadow: "0 24px 60px -24px rgba(201,168,76,0.28)" }} />
                  {/* top accent bar grows from left */}
                  <span aria-hidden className="absolute top-0 left-0 right-0 h-[2px] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300 ease-out"
                    style={{ background: "#C9A84C" }} />

                  <div className="relative flex items-center justify-between mb-7">
                    <div className="w-12 h-12 flex items-center justify-center transition-transform duration-300 ease-out group-hover:scale-110 group-hover:-rotate-3"
                      style={{ border: "1px solid rgba(201,168,76,0.22)", color: "#C9A84C" }}>
                      {feat.icon}
                    </div>
                    <span className="text-xs tabular-nums transition-colors duration-300 group-hover:text-[#C9A84C]"
                      style={{ color: "rgba(245,240,232,0.28)", fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.1em" }}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>

                  <h3 className="relative text-base font-medium mb-2 transition-colors duration-300 group-hover:text-[#C9A84C]"
                    style={{ color: "#F5F0E8", fontFamily: "'Montserrat', sans-serif" }}>{feat.title}</h3>
                  <p className="relative text-xs sm:text-sm leading-relaxed"
                    style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>{feat.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <DrawLine className="max-w-3xl mx-auto px-6" />

        {/* ══════════════════════════════════════
            PRICING TEASER
        ══════════════════════════════════════ */}
        <section id="pricing" className="py-28 px-6" aria-labelledby="pricing-heading">
          <div className="max-w-3xl mx-auto text-center" data-gsap="reveal">
            <SectionLabel>Pricing</SectionLabel>
            <SectionHeadline id="pricing-heading">
              Scale your automation,{" "}
              <em style={{ color: "#C9A84C" }}>not your costs</em>
            </SectionHeadline>
            <div className="mt-6 flex justify-center"><GoldDivider /></div>
            <p className="mt-8 text-sm leading-relaxed max-w-xl mx-auto" style={{ color: "rgba(245,240,232,0.5)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
              Plans start at $17/month with a 14-day free trial on every tier. Compare Basic, Growth and Elite in full.
            </p>
            <Link
              href="/pricing"
              className="inline-block mt-10 px-8 py-3.5 text-xs font-medium tracking-widest uppercase transition-all duration-300"
              style={{ background: "linear-gradient(135deg, #C9A84C, #E8D5A3)", color: "#0A0908", fontFamily: "'Montserrat', sans-serif" }}
            >
              View Full Pricing →
            </Link>
          </div>
        </section>

        <UpcomingFeatures />

        <DrawLine className="max-w-3xl mx-auto px-6" />

        {/* ══════════════════════════════════════
            TRUST PILLARS
        ══════════════════════════════════════ */}
        <section className="py-20 px-6" aria-label="Trust pillars">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-px" style={{ background: "rgba(201,168,76,0.07)" }}>
              {[
                { icon: <Icons.Shield />, label: "Official API Only", desc: "Built on Instagram's Graph API. No bots, no workarounds." },
                { icon: <Icons.Lock />, label: "No Password Stored", desc: "OAuth 2.0 only. Your credentials never touch our servers." },
                { icon: <Icons.Sliders />, label: "You Stay in Control", desc: "Pause, edit, or override any automation from your dashboard." },
              ].map((p) => (
                <motion.div
                  key={p.label}
                  className="p-8 text-center"
                  style={{ background: "#0A0908", color: "#C9A84C" }}
                  whileHover={{ background: "rgba(201,168,76,0.025)" }}
                >
                  <div className="flex justify-center mb-4">{p.icon}</div>
                  <p style={{ fontFamily: "'Cormorant', Georgia, serif", fontSize: "1.15rem", fontWeight: 400, color: "#F5F0E8", lineHeight: 1.2 }}>{p.label}</p>
                  <p className="mt-2 text-xs leading-relaxed" style={{ color: "rgba(245,240,232,0.35)", fontFamily: "'Montserrat', sans-serif" }}>{p.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <DrawLine className="max-w-3xl mx-auto px-6" />

        {/* ══════════════════════════════════════
            FAQ
        ══════════════════════════════════════ */}
        <section id="faq" className="py-28 px-6" aria-labelledby="faq-heading">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-16" data-gsap="reveal">
              <SectionLabel>FAQ</SectionLabel>
              <SectionHeadline id="faq-heading">Questions answered</SectionHeadline>
              <div className="mt-6"><GoldDivider /></div>
            </div>

            <dl style={{ border: "1px solid rgba(201,168,76,0.1)" }}>
              {FAQS.map((faq, i) => (
                <motion.div
                  key={faq.q}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.04, ease: [0.16, 1, 0.3, 1] }}
                  style={{ borderBottom: i < FAQS.length - 1 ? "1px solid rgba(201,168,76,0.08)" : "none" }}
                  whileHover={{ backgroundColor: "rgba(201,168,76,0.012)" }}
                >
                    <dt>
                      <button
                        onClick={() => setOpenFaq(openFaq === i ? null : i)}
                        aria-expanded={openFaq === i}
                        className="w-full text-left px-6 py-5 flex items-center justify-between gap-4 cursor-pointer"
                      >
                        <span className="text-sm font-medium" style={{ color: "#F5F0E8", fontFamily: "'Montserrat', sans-serif" }}>
                          {faq.q}
                        </span>
                        <motion.svg
                          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}
                          className="w-4 h-4 flex-shrink-0"
                          style={{ color: "#C9A84C" }}
                          animate={{ rotate: openFaq === i ? 180 : 0 }}
                          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                          aria-hidden="true"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </motion.svg>
                      </button>
                    </dt>
                    <AnimatePresence initial={false}>
                      {openFaq === i && (
                        <motion.dd
                          key="answer"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                          style={{ overflow: "hidden" }}
                        >
                          <p className="px-6 pb-5 text-sm leading-relaxed"
                            style={{ color: "rgba(245,240,232,0.42)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
                            {faq.a}
                          </p>
                        </motion.dd>
                      )}
                    </AnimatePresence>
                </motion.div>
              ))}
            </dl>
          </div>
        </section>

        {/* ══════════════════════════════════════
            FINAL CTA
        ══════════════════════════════════════ */}
        <section className="py-32 px-6 relative overflow-hidden" aria-labelledby="cta-heading">
          <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
            <motion.div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] rounded-full blur-3xl"
              style={{ background: "rgba(201,168,76,0.06)" }}
              animate={{ scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>
          <FadeIn>
            <div className="max-w-2xl mx-auto text-center relative">
              <div aria-hidden="true" className="absolute inset-x-0 top-0 h-px"
                style={{ background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.35), transparent)" }} />
              <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-px"
                style={{ background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.15), transparent)" }} />

              <p className="text-xs tracking-[0.3em] uppercase mb-8 pt-10"
                style={{ color: "#C9A84C", fontFamily: "'Montserrat', sans-serif" }}>
                Ready to turn DMs into clients?
              </p>
              <h2 id="cta-heading" style={{
                fontFamily: "'Cormorant', Georgia, serif",
                fontSize: "clamp(2.8rem, 6vw, 4.5rem)",
                fontWeight: 300, color: "#F5F0E8", lineHeight: 1.08,
              }}>
                Start your free trial,{" "}
                <em style={{ color: "#C9A84C", fontStyle: "italic" }}>scale when ready</em>
              </h2>
              <p className="mt-6 text-sm leading-relaxed max-w-sm mx-auto mb-10"
                style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
                Join brands that never miss a DM. 14-day free trial when you connect your Instagram account.
              </p>
              <motion.div whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.97 }} style={{ display: "inline-block" }}>
                <Link href="/login"
                  className="inline-flex items-center gap-2.5 px-10 py-4 text-xs font-medium tracking-[0.12em] uppercase"
                  style={{ background: "linear-gradient(135deg, #C9A84C 0%, #E8D5A3 50%, #C9A84C 100%)", color: "#0A0908", fontFamily: "'Montserrat', sans-serif" }}>
                  Get Started Free
                  <motion.span animate={{ x: [0, 4, 0] }} transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}>
                    <Icons.Arrow />
                  </motion.span>
                </Link>
              </motion.div>
              <p className="mt-6 pb-10 text-xs" style={{ color: "rgba(245,240,232,0.18)", fontFamily: "'Montserrat', sans-serif", letterSpacing: "0.05em" }}>
                14-day free trial &nbsp;·&nbsp; Cancel anytime &nbsp;·&nbsp; No hidden fees
              </p>
            </div>
          </FadeIn>
        </section>

        {/* ══════════════════════════════════════
            LATEST FROM BLOG
        ══════════════════════════════════════ */}
        <section className="py-28 px-6" aria-labelledby="blog-heading">
          <div className="max-w-6xl mx-auto">
            <FadeIn>
              <div className="text-center mb-16">
                <SectionLabel>Insights</SectionLabel>
                <SectionHeadline id="blog-heading">
                  Latest from the <span style={{ color: "#C9A84C", fontStyle: "italic" }}>Blog</span>
                </SectionHeadline>
              </div>
            </FadeIn>

            {postsLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[1, 2, 3].map((i) => (
                  <FadeIn key={i} delay={i * 0.1}>
                    <div className="animate-pulse">
                      <div className="aspect-[16/9] bg-[rgba(255,255,255,0.02)] mb-5 border border-[rgba(201,168,76,0.05)]" />
                      <div className="h-3 w-20 bg-[rgba(201,168,76,0.1)] mb-3" />
                      <div className="h-5 w-full bg-[rgba(255,255,255,0.03)] mb-2" />
                      <div className="h-10 w-full bg-[rgba(255,255,255,0.01)]" />
                    </div>
                  </FadeIn>
                ))}
              </div>
            ) : latestPosts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {latestPosts.map((post, i) => (
                  <FadeIn key={post._id} delay={i * 0.1}>
                    <Link href={`/blog/${post.slug.current}`} className="group block">
                      <div className="relative aspect-[16/9] mb-5 overflow-hidden border border-[rgba(201,168,76,0.1)]">
                        {post.mainImage ? (
                          <Image
                            src={urlForImage(post.mainImage).url()}
                            alt={post.title}
                            fill
                            sizes="(max-width: 768px) 100vw, 33vw"
                            className="object-cover opacity-60 group-hover:opacity-100 transition-all duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <div className="w-full h-full bg-[rgba(201,168,76,0.03)] flex items-center justify-center">
                            <span className="text-[10px] tracking-widest uppercase text-[rgba(201,168,76,0.3)]">AureliusCo</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-[9px] tracking-widest uppercase px-2 py-0.5" style={{ background: "rgba(201,168,76,0.12)", color: "#C9A84C" }}>
                          {post.categories?.[0]?.title || "Guide"}
                        </span>
                        <span className="text-[10px]" style={{ color: "rgba(245,240,232,0.2)" }}>
                          {new Date(post.publishedAt || post._createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        </span>
                      </div>
                      <h3 className="text-lg font-light mb-2 group-hover:text-[#C9A84C] transition-colors" style={{ fontFamily: "'Cormorant', serif", color: "#F5F0E8" }}>
                        {post.title}
                      </h3>
                      <p className="text-xs leading-relaxed line-clamp-2" style={{ color: "rgba(245,240,232,0.35)", fontFamily: "'Montserrat', sans-serif" }}>
                        {post.excerpt}
                      </p>
                    </Link>
                  </FadeIn>
                ))}
              </div>
            ) : (
              <FadeIn>
                <div className="text-center py-16 border border-[rgba(201,168,76,0.08)]" style={{ background: "rgba(201,168,76,0.02)" }}>
                  <p className="text-xs tracking-[0.3em] uppercase mb-3" style={{ color: "rgba(201,168,76,0.5)", fontFamily: "'Montserrat', sans-serif" }}>Coming Soon</p>
                  <p className="text-2xl font-light" style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Cormorant', serif" }}>Blogs Coming Soon</p>
                  <p className="text-xs mt-3" style={{ color: "rgba(245,240,232,0.2)", fontFamily: "'Montserrat', sans-serif" }}>Stay tuned for expert insights and resources.</p>
                </div>
              </FadeIn>
            )}

            <FadeIn delay={0.4}>
              <div className="mt-16 text-center">
                <Link
                  href="/blog"
                  className="inline-block text-xs tracking-widest uppercase border-b border-[rgba(201,168,76,0.3)] pb-1 transition-all hover:border-[#C9A84C] hover:text-[#C9A84C]"
                  style={{ color: "rgba(245,240,232,0.6)", fontFamily: "'Montserrat', sans-serif" }}
                >
                  View All Articles
                </Link>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* ══════════════════════════════════════
            NEWSLETTER
        ══════════════════════════════════════ */}
        <section className="py-24 px-6" style={{ background: "rgba(201,168,76,0.02)" }}>
          <div className="max-w-4xl mx-auto text-center">
            <FadeIn>
              <h2 style={{
                fontFamily: "'Cormorant', Georgia, serif",
                fontSize: "clamp(1.5rem, 3vw, 2.25rem)",
                fontWeight: 300, color: "#F5F0E8", marginBottom: "1rem"
              }}>
                Stay ahead of the <span style={{ color: "#C9A84C", fontStyle: "italic" }}>Algorithm</span>
              </h2>
              <p className="text-xs mb-10 max-w-md mx-auto" style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif", lineHeight: 1.6 }}>
                Join 2,000+ brands getting weekly insights on Instagram automation and growth.
              </p>
              <NewsletterForm />
            </FadeIn>
          </div>
        </section>
        </main>

        {/* ══════════════════════════════════════
            FOOTER
        ══════════════════════════════════════ */}
        <footer className="px-6 py-16" style={{ borderTop: "1px solid rgba(201,168,76,0.1)" }} aria-label="Site footer">
          <div className="max-w-6xl mx-auto">
            {/* Pre-footer CTA strip */}
            <div className="text-center mb-16">
              <p style={{ fontFamily: "'Cormorant', Georgia, serif", fontSize: "1.5rem", fontWeight: 300, color: "rgba(245,240,232,0.4)" }}>
                Built on Instagram's Official API
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-10 mb-12">
              <div className="col-span-2 sm:col-span-1">
                <Link href="/" className="flex items-center gap-2.5 mb-4 w-fit cursor-pointer">
                  <div className="w-7 h-7 flex items-center justify-center" style={{ border: "1px solid rgba(201,168,76,0.35)" }}>
                    <span style={{ color: "#C9A84C", fontSize: "0.75rem", fontFamily: "'Cormorant', Georgia, serif", fontWeight: 600 }}>A</span>
                  </div>
                  <span className="text-sm tracking-widest" style={{ color: "#F5F0E8", fontFamily: "'Cormorant', Georgia, serif", letterSpacing: "0.1em" }}>
                    Aurelius & Co
                  </span>
                </Link>
                <p className="text-xs leading-relaxed max-w-[180px]"
                  style={{ color: "rgba(245,240,232,0.25)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
                  Enterprise Instagram automation for brands that demand results.
                </p>
              </div>

              {[
{ heading: "Product", links: [
      { label: "Features", href: "/features" },
      { label: "Pricing", href: "/pricing" },
      { label: "Integrations", href: "/integrations" },
      { label: "Changelog", href: "/changelog" },
      { label: "Docs", href: "/docs" },
    ]},
  { heading: "Company", links: [
      { label: "About Us", href: "/about" },
      { label: "Careers", href: "/careers" },
      { label: "Blog", href: "/blog" },
      { label: "Contact", href: "/contact" },
    ]},
  { heading: "Legal", links: [
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Terms of Service", href: "/terms" },
      { label: "Cookie Policy", href: "/cookies" },
    ]},
              ].map((col) => (
                <div key={col.heading}>
                  <h3 className="text-xs font-medium uppercase tracking-widest mb-4"
                    style={{ color: "#C9A84C", fontFamily: "'Montserrat', sans-serif" }}>{col.heading}</h3>
<ul className="space-y-2.5">
      {col.links.map((link) => (
        <li key={link.label}>
          <Link href={link.href} className="text-xs transition-colors duration-200 cursor-pointer" style={{ color: "rgba(245,240,232,0.28)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }} onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "rgba(201,168,76,0.65)"; }} onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "rgba(245,240,232,0.28)"; }}>
            {link.label}
          </Link>
        </li>
      ))}
    </ul>
                </div>
              ))}
            </div>

            {/* Connect row */}
            <div className="flex items-center gap-4 mb-8">
              {[
                { label: "Instagram", href: "https://www.instagram.com/aureliusco", path: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" },
                { label: "Twitter", href: "https://twitter.com/aureliusco", path: "M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" },
                { label: "LinkedIn", href: "https://www.linkedin.com/company/aureliusco", path: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" },
              ].map((social) => (
                <a key={social.label} href={social.href} aria-label={social.label} target="_blank" rel="noopener noreferrer"
                  className="w-8 h-8 flex items-center justify-center transition-colors duration-200 cursor-pointer"
                  style={{ border: "1px solid rgba(201,168,76,0.18)", color: "rgba(245,240,232,0.3)" }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.color = "#C9A84C";
                    (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(201,168,76,0.45)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.color = "rgba(245,240,232,0.3)";
                    (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(201,168,76,0.18)";
                  }}>
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5" aria-hidden="true">
                    <path d={social.path} />
                  </svg>
                </a>
              ))}
            </div>

            <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4"
              style={{ borderTop: "1px solid rgba(201,168,76,0.07)" }}>
              <p className="text-xs" style={{ color: "rgba(245,240,232,0.18)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
                © {new Date().getFullYear()} Aurelius & Co. All rights reserved.
              </p>
              <p className="text-xs" style={{ color: "rgba(245,240,232,0.12)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
                Not affiliated with Instagram or Meta Platforms, Inc.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
